<script setup lang="ts">
import { computed, inject, reactive, ref, Ref, onMounted, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { LANG_ENGLISH, LANG_FRENCH, VC_CREATED } from '@/constants';
import { useVoucherStore } from '@/store/VoucherStore';
import { useItemConfigurationStore } from '@/store/ItemConfigurationStore';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const id = dialogRef.value.data.quoteId;
const bookingNumber = dialogRef.value.data.bookingId;
const isSave = dialogRef?.value.options.emits.isSave;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const voucherStore = useVoucherStore();
const configurationModule = useItemConfigurationStore();
const userInfo = useUserInfo();
const isLoading = ref(false);
const currentUser = ref<UserInfo>();
const minDateRangeValue = ref(new Date());

const language = ref([
  { name: t('promos.labels.english'), value: LANG_ENGLISH },
  { name: t('promos.labels.french'), value: LANG_FRENCH }
]);

const signatureOptions = computed(() => configurationModule.selectSignatureOptions);

const validateEmail = (value) => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@([a-zA-Z\d-]+\.)+[a-zA-Z]{2,}$/.test(value);

const form = reactive({
  bookingNumber: bookingNumber,
  value: 0,
  name: '',
  email: '',
  depDate: null,
  expiryDate: '',
  signatureOwner: '',
  lang: '',
  created: new Date(),
  createdBy: '',
  id: id.substring(2, id.length),
  status: VC_CREATED
});

const rules = {
  bookingNumber: {
    required
  },
  value: {
    required
  },
  name: {
    required
  },
  email: {
    required,
    validateEmail
  },
  depDate: {
    required
  },
  expiryDate: {
    required
  },
  signatureOwner: {
    required
  },
  lang: {
    required
  }
};

const v$ = useVuelidate(rules, form);
const submit = () => {
  v$.value.$validate();
  if (!v$.value.$error) {
    const payload = { ...form, createdBy: toValue(currentUser)?.name };
    payload.expiryDate = formatDate(payload.expiryDate, 'yyyy-LL-dd');
    payload.created = formatDate(new Date(), 'yyyy-LL-dd');
    payload.depDate = formatDate(payload.depDate, 'yyyy-LL-dd');
    isLoading.value = true;

    voucherStore
      .handleSubmit(payload, id)
      .then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        isSave();
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
};

const formatDate = (date, format) => {
  return date ? DateTime.fromISO(new Date(date).toISOString()).toFormat(format) : '';
};

const onDateChange = (event) => {
  const expiry_date = DateTime.fromISO(new Date(event).toISOString()).plus({ years: 1 });
  form.expiryDate = formatDate(expiry_date, 'yyyy/LL/dd');
};

onMounted(async () => {
  currentUser.value = await userInfo.getUserInfo();
});
</script>
<template>
  <div class="create-voucher-modal">
    <LoadingSpinner v-if="isLoading" />
    <div>
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="booking-number">{{ $t('voucher.booking-number') }}</label>
            <InputText id="booking-number" v-model="form.bookingNumber" disabled :class="v$.bookingNumber.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.bookingNumber.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col">
            <label for="voucher-amount">{{ $t('voucher.voucher-amount') }}</label>
            <InputNumber id="voucher-amount" v-model="form.value" :minFractionDigits="2" :maxFractionDigits="2" :class="v$.value.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.value.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
    </div>
    <div class="mt-5">
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="client-name">{{ $t('voucher.client-name') }}</label>
            <InputText id="client-name" v-model="form.name" :class="v$.name.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.name.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
    </div>
    <div class="mt-5">
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="client-email">{{ $t('voucher.client-email') }}</label>
            <InputText id="client-email" v-model="form.email" :class="v$.email.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.email.$error && !form.email">{{ $t('validate.required') }}</p>
            <p class="text-red-500 pt-1" v-if="v$.email.$error && form.email">{{ $t('voucher.tableHeader.email_invalid') }}</p>
          </div>
        </div>
      </Fluid>
    </div>
    <div class="mt-5">
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="departure-date">{{ $t('create_quote.departure_date') }}</label>
            <DatePicker id="departure-date" v-model="form.depDate" dateFormat="yy/mm/dd" :minDate="minDateRangeValue" @update:modelValue="onDateChange($event)" :class="v$.depDate.$error ? 'p-invalid' : ''">
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
            <p class="text-red-500 pt-1" v-if="v$.depDate.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col">
            <label for="expiry-date">{{ $t('voucher.expiry-date') }}</label>
            <InputText id="expiry-date" v-model="form.expiryDate" disabled :class="v$.expiryDate.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.expiryDate.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
    </div>
    <div class="mt-5">
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="signature">{{ $t('voucher.signature') }}</label>
            <Select id="signature" v-model="form.signatureOwner" :options="signatureOptions" optionLabel="name" optionValue="imgUrl" :class="v$.signatureOwner.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.signatureOwner.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col">
            <label for="language">{{ $t('voucher.language') }}</label>
            <Select id="language" v-model="form.lang" :options="language" optionLabel="name" optionValue="value" :class="v$.lang.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.lang.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
    </div>
    <div class="btn-center">
      <Button id="submit-btn" :label="$t('voucher.submit')" @click="submit()" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.btn-center {
  display: flex;
  justify-content: center;
}

.create-voucher-modal {
  width: max(500px, 50vw);
}
</style>
